import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Collapse,
  List,
  ListItem,
  CircularProgress,
  TextField,
  Button,
} from '@mui/material';
import { Tooltip } from '@mui/material';
import {
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Article as ArticleIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../axiosInstance';

export const OnboardingProcess = ({ onboardingStatus, processesList, finishedCounts, skipWebScrape, reportId, traceId, onTraceIdClick, apiKey }) => {
  const [openLogs, setOpenLogs] = useState(null);
  const [expandedLogs, setExpandedLogs] = useState({});
  const [processLogsDialog, setProcessLogsDialog] = useState({ open: false, logs: [], loading: false, logType: 'Info', totalErrors: 0 });
  const [processLogsSearch, setProcessLogsSearch] = useState('');
  const filteredLogs = useMemo(() => {
    if (!processLogsSearch) return processLogsDialog.logs;
    const search = processLogsSearch.toLowerCase();
    return processLogsDialog.logs.filter((log) =>
      (log.message && log.message.toLowerCase().includes(search)) ||
      (log.productId && log.productId.toLowerCase().includes(search)) ||
      (log.logType && log.logType.toLowerCase().includes(search))
    );
  }, [processLogsDialog.logs, processLogsSearch]);

  // Cache the last valid data to prevent disappearing UI
  const lastValidProcessesList = useRef(null);
  const lastValidOnboardingStatus = useRef(null);
  const lastReportId = useRef(reportId);

  // Clear cache when reportId changes (user selected different row)
  useEffect(() => {
    if (reportId !== lastReportId.current) {
      lastValidProcessesList.current = null;
      lastValidOnboardingStatus.current = null;
      lastReportId.current = reportId;
    }
  }, [reportId]);

  useEffect(() => {
    if (processesList === null) {
      lastValidProcessesList.current = null;
    } else if (processesList && processesList.length > 0) {
      lastValidProcessesList.current = processesList;
    }
  }, [processesList]);

  useEffect(() => {
    if (onboardingStatus === null) {
      lastValidOnboardingStatus.current = null;
    } else if (onboardingStatus && Object.keys(onboardingStatus).length > 0) {
      lastValidOnboardingStatus.current = onboardingStatus;
    }
  }, [onboardingStatus]);

  // Use cached data if current data is empty/null
  const displayProcessesList = processesList === null
    ? null
    : (processesList && processesList.length > 0)
      ? processesList
      : lastValidProcessesList.current;

  const displayOnboardingStatus = onboardingStatus === null
    ? null
    : (onboardingStatus && Object.keys(onboardingStatus).length > 0)
      ? onboardingStatus
      : lastValidOnboardingStatus.current;

  const formatLogTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const toggleLogsExpanded = (key) => {
    setExpandedLogs((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const calculateElapsedTime = (startTime, finishTime) => {
    if (!startTime) return '';
    const start = new Date(startTime).getTime();
    const end = finishTime ? new Date(finishTime).getTime() : Date.now();
    const diffMs = end - start;
    if (diffMs < 0) return '';

    const seconds = Math.floor(diffMs / 1000) % 60;
    const minutes = Math.floor(diffMs / (1000 * 60)) % 60;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));

    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  const fetchProcessLogs = async (eventProcessId, logType = 'Info', totalErrors = 0) => {
    setProcessLogsSearch('');
    setProcessLogsDialog({ open: true, logs: [], loading: true, logType, totalErrors });
    try {
      const response = await axiosInstance.get(
        `/onboardingTenant/process-logs/${eventProcessId}/${logType}`,
        { headers: { 't-api-key': apiKey } }
      );
      setProcessLogsDialog({ open: true, logs: response.data || [], loading: false, logType, totalErrors });
    } catch (error) {
      console.error('Error fetching process logs:', error);
      setProcessLogsDialog({ open: true, logs: [], loading: false, logType, totalErrors });
    }
  };

  const downloadLogsAsJson = () => {
    const blob = new Blob([JSON.stringify(processLogsDialog.logs, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `process-logs-${new Date().getTime()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadAllData = (data) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `onboarding-progress-${reportId || 'summary'}-${new Date().getTime()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const highlightText = (text, search) => {
    if (!search || !text) return text;
    const regex = new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} style={{ backgroundColor: '#fff176', padding: 0 }}>{part}</mark>
      ) : (
        part
      )
    );
  };

  const getLogTypeColor = (type) => {
    if (type && type.toLowerCase() === 'error') {
      return '#f44336';
    }
    return 'inherit';
  };

  const getLogBackgroundColor = (type) => {
    if (type && type.toLowerCase() === 'error') {
      return '#ffebee';
    }
    return '#f9f9f9';
  };

  const getLogBorderColor = (type) => {
    if (type && type.toLowerCase() === 'error') {
      return '#f44336';
    }
    return '#1976d2';
  };

  const renderProcessCard = (data, label, key) => {
    const {
      statusImport = 'Not Started',
      errors = [],
      messageProcessStatus = '',
      processStatusLogs = {},
      processLogs = [],
      lastLogMessage = '',
      lastErrorMessage = '',
      totalTime = '',
      totalErrors = 0,
      startTime = '',
      finishedCount = 0,
      lastLog = null,
    } = data;
    const isExpanded = expandedLogs[key];
    const hasArrayLogs = Array.isArray(processLogs) && processLogs.length > 0;
    const hasObjectLogs = Object.keys(processStatusLogs).length > 0;
    const logsCount = hasArrayLogs
      ? processLogs.length
      : hasObjectLogs
        ? Object.keys(processStatusLogs).length
        : 0;
    const isStale = (() => {
      if (!startTime) return false;
      const lowerStatus = (statusImport || '').toLowerCase();
      if (lowerStatus === 'finished' || lowerStatus === 'error' || lowerStatus === 'failed') return false;
      const start = new Date(startTime).getTime();
      return !isNaN(start) && (Date.now() - start) > 14400000;
    })();

    const displayStatus = isStale ? 'Unresponsive' : statusImport;

    const getBorderColor = (status) => {
      const lowerStatus = (status || '').toLowerCase();
      if (lowerStatus === 'finished') return 'green';
      if (lowerStatus === 'started') return '#d4a300';
      if (lowerStatus === 'inprogress' || lowerStatus === 'in progress') return '#e0b014';
      if (lowerStatus === 'not started' || lowerStatus === 'notstarted') return '#9e9e9e';
      if (lowerStatus === 'error' || lowerStatus === 'failed' || lowerStatus === 'unresponsive') return '#f44336';
      return '#1976d2';
    };

    return (
      <Paper
        key={key}
        sx={{
          p: 2,
          mb: 2,
          borderLeft: `6px solid ${getBorderColor(displayStatus)}`,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
              {label}
            </Typography>
            {!lastLog && lastLogMessage && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                — {lastLogMessage}
              </Typography>
            )}
            {!lastLog && lastErrorMessage && (
              <Tooltip title={lastErrorMessage.length > 100 ? lastErrorMessage : ''}>
                <Typography variant="body2" color="error" sx={{ fontStyle: 'italic', ml: 1 }}>
                  — {lastErrorMessage.length > 100 ? lastErrorMessage.substring(0, 100) + '...' : lastErrorMessage}
                </Typography>
              </Tooltip>
            )}
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title="View Process Logs">
              <IconButton aria-label="View Process Logs" onClick={() => fetchProcessLogs(key, 'Info')} size="small">
                <ArticleIcon color="primary" />
              </IconButton>
            </Tooltip>
            {totalErrors != null && totalErrors > 0 && (
              <Tooltip title="View Error Logs">
                <IconButton aria-label="View Error Logs" onClick={() => fetchProcessLogs(key, 'Error', totalErrors)} size="small">
                  <ErrorOutlineIcon color="error" />
                </IconButton>
              </Tooltip>
            )}
            {logsCount > 0 && (
              <IconButton aria-label="Toggle Logs" onClick={() => toggleLogsExpanded(key)} size="small">
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                <Typography variant="caption" sx={{ ml: 0.5 }}>
                  {logsCount} logs
                </Typography>
              </IconButton>
            )}
            {hasObjectLogs && (
              <IconButton aria-label="View Logs Dialog" onClick={() => setOpenLogs(processStatusLogs)}>
                <InfoIcon color="primary" />
              </IconButton>
            )}
          </Box>
        </Box>
        <Typography variant="body2" gutterBottom>
          Status: <strong style={{ color: getBorderColor(displayStatus) }}>{displayStatus}</strong>
        </Typography>
        {startTime && (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" gap={2} alignItems="center">
              <Typography variant="body2" gutterBottom sx={{ mb: 0 }}>
                Started: <strong>{new Date(startTime).toLocaleString()}</strong>
              </Typography>
              {finishedCount > 0 && (
                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 0 }}>
                  Processed: <strong>{finishedCount}</strong>
                </Typography>
              )}
            </Box>
            {totalTime && displayStatus !== 'Unresponsive' && (
              <Typography variant="body2" gutterBottom sx={{ mb: 0 }}>
                Total Time: <strong>{totalTime}</strong>
              </Typography>
            )}
          </Box>
        )}
        {!startTime && totalTime && displayStatus !== 'Unresponsive' && (
          <Box display="flex" justifyContent="flex-end">
            <Typography variant="body2" gutterBottom>
              Total Time: <strong>{totalTime}</strong>
            </Typography>
          </Box>
        )}
        {messageProcessStatus && (
          <Typography variant="body2" gutterBottom>
            Message: {messageProcessStatus}
          </Typography>
        )}
        {errors && errors.length > 0 && (
          <Typography variant="body2" color="error">
            Errors: {errors.join(', ')}
          </Typography>
        )}

        {lastLog && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              backgroundColor: getLogBackgroundColor(lastLog.type),
              borderRadius: 1,
              borderLeft: `3px solid ${getLogBorderColor(lastLog.type)}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 'bold',
                display: 'block',
                mb: 0.5,
                color: getLogTypeColor(lastLog.type),
              }}
            >
              Latest: {formatLogTimestamp(lastLog.timestamp)}
            </Typography>
            <Typography variant="body2" sx={{ color: getLogTypeColor(lastLog.type) }}>
              {lastLog.message}
            </Typography>
          </Box>
        )}

        {hasArrayLogs && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 2, backgroundColor: '#f5f5f5', borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                All Process Logs:
              </Typography>
              <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                {processLogs.map((log, index) => (
                  <ListItem
                    key={log.id || index}
                    sx={{
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      borderBottom: index < processLogs.length - 1 ? '1px solid #e0e0e0' : 'none',
                      py: 1,
                      backgroundColor:
                        log.type && log.type.toLowerCase() === 'error' ? '#ffebee' : 'transparent',
                      borderRadius: log.type && log.type.toLowerCase() === 'error' ? 1 : 0,
                      px: log.type && log.type.toLowerCase() === 'error' ? 2 : 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 'bold',
                        color: getLogTypeColor(log.type),
                      }}
                    >
                      {formatLogTimestamp(log.timestamp)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        color: getLogTypeColor(log.type),
                      }}
                    >
                      {log.message}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Collapse>
        )}
      </Paper>
    );
  };

  const steps = useMemo(() => {
    let processSteps = [
      { name: 'autoGeneratingSettings', label: 'Auto Generating Settings' },
      { name: 'autoGeneratingPrompts', label: 'Auto Generating Prompts' },
      { name: 'policy', label: 'Policy' },
      { name: 'downloader', label: 'Downloader' },
      { name: 'enrichment', label: 'Enrichment' },
      { name: 'aggregation', label: 'Aggregation' },
      { name: 'converter', label: 'Converter' },
      { name: 'mapper', label: 'Mapper' },
      { name: 'embedder', label: 'Embedder' },
      { name: 'indexer', label: 'Indexer' },
      { name: 'webScraper', label: 'Web Scraper' },
    ];
    const dataFeedType = displayOnboardingStatus?.tenant?.DataFeedConnector?.Configurations?.dataFeed;
    if (skipWebScrape) {
      processSteps = processSteps.filter(({ name }) => name !== 'webScraper');
    }
    if (dataFeedType === 'Shopify') {
      processSteps = processSteps.filter(({ name }) => name !== 'converter');
    }
    return processSteps;
  }, [skipWebScrape, displayOnboardingStatus]);

  if (displayProcessesList && displayProcessesList.length > 0) {
    const processOrder = { 'tenantsettings': 0, 'generateprompts': 1, 'importpolicy': 2, 'downloader': 3, 'enrichment': 4, 'aggregation': 5, 'converter': 6, 'mapper': 7, 'embedder': 8, 'indexer': 9 };
    const sortedProcessesList = [...displayProcessesList].sort((a, b) => {
      const orderA = processOrder[(a.name || '').toLowerCase()] !== undefined ? processOrder[(a.name || '').toLowerCase()] : 99;
      const orderB = processOrder[(b.name || '').toLowerCase()] !== undefined ? processOrder[(b.name || '').toLowerCase()] : 99;
      return orderA - orderB;
    });

    const allFinished = sortedProcessesList.every((p) => {
      const s = (p.statusImport || p.status || '').toLowerCase();
      return s === 'finished' || s === 'failed';
    });

    const totalImportTime = allFinished ? (() => {
      const intervals = sortedProcessesList
        .filter((p) => p.startTime)
        .map((p) => ({
          start: new Date(p.startTime).getTime(),
          end: p.finishTime ? new Date(p.finishTime).getTime() : Date.now(),
        }))
        .sort((a, b) => a.start - b.start);

      if (intervals.length === 0) return null;

      const merged = [intervals[0]];
      for (let i = 1; i < intervals.length; i++) {
        const last = merged[merged.length - 1];
        if (intervals[i].start <= last.end) {
          last.end = Math.max(last.end, intervals[i].end);
        } else {
          merged.push(intervals[i]);
        }
      }

      const totalMs = merged.reduce((acc, iv) => acc + (iv.end - iv.start), 0);
      const seconds = Math.floor(totalMs / 1000) % 60;
      const minutes = Math.floor(totalMs / (1000 * 60)) % 60;
      const hours = Math.floor(totalMs / (1000 * 60 * 60));
      if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
      if (minutes > 0) return `${minutes}m ${seconds}s`;
      return `${seconds}s`;
    })() : null;

    return (
      <>
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6" fontWeight="bold">
                Progress {reportId && `- Process ID: ${reportId}`}
              </Typography>
              <Tooltip title="Download all processes data">
                <IconButton size="small" onClick={() => downloadAllData(sortedProcessesList)}>
                  <DownloadIcon fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
            </Box>
            {totalImportTime && (
              <Typography variant="body2" fontWeight="bold" color="text.secondary">
                Total Import Time: {totalImportTime}
              </Typography>
            )}
          </Box>
          {traceId && (
            <Tooltip title="Click to view Agents Trace logs">
              <Typography
                variant="body2"
                mb={2}
                sx={{
                  color: "#1976d2",
                  cursor: "pointer",
                  fontFamily: "monospace",
                  "&:hover": { textDecoration: "underline" },
                  display: "inline-block",
                }}
                onClick={() => onTraceIdClick && onTraceIdClick(traceId)}
              >
                Trace ID: {traceId}
              </Typography>
            </Tooltip>
          )}
          {sortedProcessesList.map((p) => {
            const rawStatus = p.statusImport || p.status || 'NotStarted';
            const mappedLogs = (p.logs || p.processLogs || []).map(l => ({
              ...l,
              timestamp: l.timestamp || l.timeStamp,
            }));

            let count = 0;
            if (finishedCounts && p.name) {
              const processKey = p.name.toLowerCase();
              const finishedKeyAliases = { webscraper: "scraper" };
              const resolvedKey = finishedKeyAliases[processKey] || processKey;

              const countKey = Object.keys(finishedCounts).find(k => k.toLowerCase() === `${resolvedKey}finished`);
              if (countKey) {
                count = finishedCounts[countKey];
              }
            }

            const uiData = {
              statusImport: rawStatus,
              messageProcessStatus: p.timeSinceProcessStart ? `Time: ${p.timeSinceProcessStart}` : (p.timeSpanString ? `Time: ${p.timeSpanString}` : null),
              errors: p.errors || [],
              processStatusLogs: {},
              processLogs: mappedLogs,
              lastLogMessage: p.lastLogMessage || p.lasLogMessage || '',
              lastErrorMessage: p.lastErrorMessage || p.lasErrorMessage || '',
              totalErrors: p.totalErrors || 0,
              totalTime: calculateElapsedTime(p.startTime, p.finishTime),
              startTime: p.startTime,
              finishedCount: count,
            };
            return renderProcessCard(uiData, p.name, p.id);
          })}
        </Box>
        <Dialog
          open={Boolean(openLogs)}
          onClose={() => setOpenLogs(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "28px",
              border: "1px solid rgba(255, 132, 76, 0.12)",
              background:
                "radial-gradient(circle at top center, rgba(255, 191, 154, 0.20), rgba(255,255,255,0.98) 38%), linear-gradient(180deg, rgba(255, 248, 243, 0.98), rgba(255,255,255,0.96))",
              boxShadow: "0 30px 70px rgba(236, 123, 55, 0.16)",
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle
            sx={{
              m: 0,
              p: 2.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid rgba(255, 132, 76, 0.10)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "14px",
                  background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
                  boxShadow: "0 14px 28px rgba(255, 139, 71, 0.24)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  flexShrink: 0,
                }}
              >
                <ArticleIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#202531", lineHeight: 1.1 }}>
                  Process Logs
                </Typography>
                <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.35 }}>
                  Inspect structured process log details.
                </Typography>
              </Box>
            </Box>
            <IconButton
              aria-label="close"
              onClick={() => setOpenLogs(null)}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "14px",
                color: "#6b7280",
                "&:hover": {
                  backgroundColor: "#fff7f1",
                  color: "#f97316",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              p: 2.5,
              backgroundColor: "transparent",
            }}
          >
            {openLogs ? (
              <Box
                sx={{
                  p: 2,
                  borderRadius: "22px",
                  border: "1px solid rgba(255, 132, 76, 0.10)",
                  backgroundColor: "rgba(255,255,255,0.82)",
                  boxShadow: "0 18px 38px rgba(236, 123, 55, 0.07)",
                }}
              >
                {Object.entries(openLogs).map(([timestamp, log], index) => (
                  <DialogContentText
                    key={timestamp}
                    sx={{
                      mb: index === Object.entries(openLogs).length - 1 ? 0 : 1.25,
                      pb: index === Object.entries(openLogs).length - 1 ? 0 : 1.25,
                      borderBottom: index === Object.entries(openLogs).length - 1 ? "none" : "1px solid rgba(255, 132, 76, 0.10)",
                      color: "#202531",
                    }}
                  >
                    <strong>{timestamp}:</strong> {log}
                  </DialogContentText>
                ))}
              </Box>
            ) : null}
          </DialogContent>
        </Dialog>
        <Dialog
          open={processLogsDialog.open}
          onClose={() => setProcessLogsDialog({ open: false, logs: [], loading: false })}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "28px",
              border: "1px solid rgba(255, 132, 76, 0.12)",
              background:
                "radial-gradient(circle at top center, rgba(255, 191, 154, 0.20), rgba(255,255,255,0.98) 38%), linear-gradient(180deg, rgba(255, 248, 243, 0.98), rgba(255,255,255,0.96))",
              boxShadow: "0 30px 70px rgba(236, 123, 55, 0.16)",
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle
            sx={{
              m: 0,
              p: 2.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: "1px solid rgba(255, 132, 76, 0.10)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "14px",
                  background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
                  boxShadow: "0 14px 28px rgba(255, 139, 71, 0.24)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  flexShrink: 0,
                }}
              >
                <ArticleIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#202531", lineHeight: 1.1 }}>
                  Process Logs {processLogsDialog.logType === 'Error' ? `(Errors: ${processLogsDialog.totalErrors})` : ''}
                </Typography>
                <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.35 }}>
                  Search, download, and inspect detailed process log events.
                </Typography>
              </Box>
            </Box>
            <Box>
              <Tooltip title="Download as JSON">
                <IconButton
                  aria-label="download"
                  onClick={downloadLogsAsJson}
                  disabled={processLogsDialog.logs.length === 0}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "14px",
                    color: "#6b7280",
                    "&:hover": {
                      backgroundColor: "#fff7f1",
                      color: "#f97316",
                    },
                  }}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <IconButton
                aria-label="close"
                onClick={() => setProcessLogsDialog({ open: false, logs: [], loading: false })}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "14px",
                  color: "#6b7280",
                  "&:hover": {
                    backgroundColor: "#fff7f1",
                    color: "#f97316",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent
            sx={{
              p: 2.5,
              backgroundColor: "transparent",
            }}
          >
            {processLogsDialog.loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : processLogsDialog.logs.length === 0 ? (
              <Typography variant="body2" color="text.secondary" py={2}>
                No logs available for this process.
              </Typography>
            ) : (
              <Box
                sx={{
                  p: 2,
                  borderRadius: "22px",
                  border: "1px solid rgba(255, 132, 76, 0.10)",
                  backgroundColor: "rgba(255,255,255,0.82)",
                  boxShadow: "0 18px 38px rgba(236, 123, 55, 0.07)",
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search logs..."
                    value={processLogsSearch}
                    onChange={(e) => setProcessLogsSearch(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        minHeight: 48,
                        borderRadius: "14px",
                        backgroundColor: "#fff",
                        "& fieldset": { borderColor: "rgba(15, 23, 42, 0.08)" },
                        "&:hover fieldset": { borderColor: "rgba(249, 115, 22, 0.35)" },
                        "&.Mui-focused fieldset": { borderColor: "#fb923c", borderWidth: "1px" },
                      },
                    }}
                  />
                  {processLogsSearch && (
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      {filteredLogs.length} hits
                    </Typography>
                  )}
                </Box>
                <List
                  dense
                  sx={{
                    maxHeight: 500,
                    overflow: 'auto',
                    borderRadius: "16px",
                    border: "1px solid rgba(255, 132, 76, 0.10)",
                    backgroundColor: "rgba(255, 250, 246, 0.72)",
                    px: 1,
                  }}
                >
                  {filteredLogs.map((log) => (
                    <ListItem
                      key={log.id}
                      sx={{
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        borderBottom: '1px solid rgba(255, 132, 76, 0.10)',
                        py: 1,
                        backgroundColor: log.logType && log.logType.toLowerCase() === 'error' ? 'rgba(254, 242, 242, 0.96)' : 'rgba(255,255,255,0.86)',
                        borderRadius: 2,
                        px: 1.25,
                        mb: 0.85,
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" width="100%">
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 'bold',
                            color: log.logType && log.logType.toLowerCase() === 'error' ? '#f44336' : 'inherit',
                          }}
                        >
                          {formatLogTimestamp(log.timeStamp)}
                        </Typography>
                        {log.logType && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 'bold',
                              color: log.logType.toLowerCase() === 'error' ? '#dc2626' : '#f97316',
                            }}
                          >
                            {log.logType}
                          </Typography>
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.5,
                          color: log.logType && log.logType.toLowerCase() === 'error' ? '#f44336' : 'inherit',
                        }}
                      >
                        {highlightText(log.message, processLogsSearch)}
                      </Typography>
                      {log.productId && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          Product: {highlightText(log.productId, processLogsSearch)}
                        </Typography>
                      )}
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  if (displayOnboardingStatus) {
    const sortedSteps = [...steps].sort((a, b) => {
      const getPriority = (status) => {
        if (status === 'Finished') return 1;
        if (status === 'Started' || status === 'InProgress' || status === 'In Progress') return 2;
        return 3;
      };

      const priorityA = getPriority(displayOnboardingStatus[a.name]?.statusImport);
      const priorityB = getPriority(displayOnboardingStatus[b.name]?.statusImport);

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      const startA = displayOnboardingStatus[a.name]?.startTime;
      const startB = displayOnboardingStatus[b.name]?.startTime;

      if (!startA && !startB) return 0;
      if (!startA) return 1;
      if (!startB) return -1;

      return new Date(startA).getTime() - new Date(startB).getTime();
    });

    return (
      <>
        <Box>
          <Box display="flex" alignItems="center" gap={0.5} mt={2} mb={1}>
            <Typography variant="subtitle1" fontWeight="bold">
              Onboarding Progress
            </Typography>
            {reportId && (
              <Typography variant="caption" color="text.secondary">
                — {reportId}
              </Typography>
            )}
            <Tooltip title="Download all status data">
              <IconButton size="small" onClick={() => downloadAllData(displayOnboardingStatus)} sx={{ p: 0.5 }}>
                <DownloadIcon fontSize="small" color="primary" />
              </IconButton>
            </Tooltip>
          </Box>
          {traceId && (
            <Tooltip title="Click to view Agents Trace logs">
              <Typography
                variant="caption"
                mb={1}
                sx={{
                  color: "#1976d2",
                  cursor: "pointer",
                  fontFamily: "monospace",
                  "&:hover": { textDecoration: "underline" },
                  display: "inline-block",
                }}
                onClick={() => onTraceIdClick && onTraceIdClick(traceId)}
              >
                Trace: {traceId}
              </Typography>
            </Tooltip>
          )}
          {sortedSteps.map(({ name, label }) => {
            const process = displayOnboardingStatus[name] || {};

            let count = 0;
            if (finishedCounts && name) {
              const processKey = name.toLowerCase();
              const finishedKeyAliases = { webscraper: "scraper" };
              const resolvedKey = finishedKeyAliases[processKey] || processKey;
              const countKey = Object.keys(finishedCounts).find(k => k.toLowerCase() === `${resolvedKey}finished`);
              if (countKey) {
                count = finishedCounts[countKey];
              }
            }

            const uiProcess = { ...process, finishedCount: count };
            return renderProcessCard(uiProcess, label, name);
          })}
        </Box>
        <Dialog open={Boolean(openLogs)} onClose={() => setOpenLogs(null)} maxWidth="md" fullWidth>
          <DialogTitle>Process Logs</DialogTitle>
          <DialogContent>
            {openLogs &&
              Object.entries(openLogs).map(([timestamp, log]) => (
                <DialogContentText key={timestamp}>
                  <strong>{timestamp}:</strong> {log}
                </DialogContentText>
              ))}
          </DialogContent>
        </Dialog>
        <Dialog open={processLogsDialog.open} onClose={() => setProcessLogsDialog({ open: false, logs: [], loading: false })} maxWidth="md" fullWidth>
          <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Process Logs {processLogsDialog.logType === 'Error' ? `(Errors: ${processLogsDialog.totalErrors})` : ''}
            <Box>
              <Tooltip title="Download as JSON">
                <IconButton aria-label="download" onClick={downloadLogsAsJson} disabled={processLogsDialog.logs.length === 0}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <IconButton aria-label="close" onClick={() => setProcessLogsDialog({ open: false, logs: [], loading: false })}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {processLogsDialog.loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : processLogsDialog.logs.length === 0 ? (
              <Typography variant="body2" color="text.secondary" py={2}>
                No logs available for this process.
              </Typography>
            ) : (
              <>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search logs..."
                    value={processLogsSearch}
                    onChange={(e) => setProcessLogsSearch(e.target.value)}
                  />
                  {processLogsSearch && (
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      {filteredLogs.length} hits
                    </Typography>
                  )}
                </Box>
                <List dense sx={{ maxHeight: 500, overflow: 'auto' }}>
                  {filteredLogs.map((log) => (
                    <ListItem
                      key={log.id}
                      sx={{
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        borderBottom: '1px solid #e0e0e0',
                        py: 1,
                        backgroundColor: log.logType && log.logType.toLowerCase() === 'error' ? '#ffebee' : 'transparent',
                        borderRadius: log.logType && log.logType.toLowerCase() === 'error' ? 1 : 0,
                        px: 1,
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" width="100%">
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 'bold',
                            color: log.logType && log.logType.toLowerCase() === 'error' ? '#f44336' : 'inherit',
                          }}
                        >
                          {formatLogTimestamp(log.timeStamp)}
                        </Typography>
                        {log.logType && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 'bold',
                              color: log.logType.toLowerCase() === 'error' ? '#f44336' : '#1976d2',
                            }}
                          >
                            {log.logType}
                          </Typography>
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.5,
                          color: log.logType && log.logType.toLowerCase() === 'error' ? '#f44336' : 'inherit',
                        }}
                      >
                        {highlightText(log.message, processLogsSearch)}
                      </Typography>
                      {log.productId && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          Product: {highlightText(log.productId, processLogsSearch)}
                        </Typography>
                      )}
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return null;
};
